import React, { useState, useEffect, useRef } from "react";
import { Grid, Input, Icon } from "semantic-ui-react";
import "semantic-ui-css/semantic.min.css";
import "react-lazy-load-image-component/src/effects/blur.css";
import { useLoginCheck } from "../../../../hooks/useLoginCheck";
import { useLoading } from "../../../../contexts/LoadingContext";
import "../../../../styles/scss/recital2026HoodieOrderList.scss";

function HoodieOrderListPage() {
  useLoginCheck();
  const { setIsLoading } = useLoading();
  const [orders, setOrders] = useState([]); // オーダー
  const [filteredOrders, setFilteredOrders] = useState([]); // 絞り込んだオーダー
  const [searchKeyword, setSearchKeyword] = useState(""); // 検索ワード
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  }); // ソート
  const searchInputRef = useRef(null);
  const [deliveredAnimeOn, setDeliveredAnimeOn] = useState("");

  // 注文一覧の取得
  const fetchOrdersData = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_recital2026_hoodie_orders/`);
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();

      console.log(data);

      setOrders(data.orders);
      setFilteredOrders(data.orders);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
    setIsLoading(false);
  };

  // 初期表示時に注文のリストを取得
  useEffect(() => {
    fetchOrdersData();
  }, []);

  // ソート
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedOrders = React.useMemo(() => {
    let sortableOrders = [...filteredOrders];
    if (sortConfig.key !== null) {
      sortableOrders.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // itemsのソート処理
        if (sortConfig.key === "items") {
          aValue = a.items.join(", ");
          bValue = b.items.join(", ");
        }

        // ソート比較
        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableOrders;
  }, [filteredOrders, sortConfig]);

  // 検索
  const handleSearch = (event) => {
    let keyword = searchKeyword;

    if (event.type === "click" || event.key === "Enter") {
      const filtered = orders.filter((order) => {
        return (
          (order.order_number && order.order_number.includes(keyword)) ||
          (order.customer_name && order.customer_name.includes(keyword)) ||
          (order.customer_phone && order.customer_phone.includes(keyword)) ||
          (order.customer_email && order.customer_email.includes(keyword)) ||
          (order.items && order.items.some((item) => item.includes(keyword))) ||
          (order.total_amount && order.total_amount.toString().includes(keyword)) ||
          (order.payment_status_display && order.payment_status_display.includes(keyword))
        );
      });
      setFilteredOrders(filtered);
    }
  };

  // 配送状態の更新
  const handleDeliveryStatusChange = async (orderId, currentStatus) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const newStatus = currentStatus === "delivered" ? "pending" : "delivered";

      const response = await fetch(`${apiUrl}/api/update_recital2026_hoodie_delivery_status/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          order_id: orderId,
          delivery_status: newStatus,
          delivered_by: "管理者" // 必要に応じて変更
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }

      setDeliveredAnimeOn("on");

      const updatedOrders = orders.map((order) => {
        if (order.id === orderId) {
          return {
            ...order,
            delivery_status: newStatus,
            delivered_at: data.delivered_at || order.delivered_at,
          };
        }
        return order;
      });

      setOrders(updatedOrders);
      setFilteredOrders(updatedOrders);
    } catch (error) {
      alert(error);
      console.error("There was a problem with the fetch operation:", error);
    }
  };

  return (
    <>
      <p className={`save-anime ${deliveredAnimeOn}`}>受渡状態を更新</p>
      <Grid>
        <h1>発表会2026 アパレル注文一覧</h1>
        <Input
          icon={{
            name: "search",
            circular: true,
            link: true,
            onClick: handleSearch,
          }}
          placeholder="Search..."
          value={searchKeyword}
          ref={searchInputRef}
          onChange={(e) => {
            setSearchKeyword(e.target.value);
          }}
          onKeyDown={handleSearch}
        />
      </Grid>

      <Grid className="hoodie-order-list">
        <Grid.Column width={16}>
          <ul className="list-grid list-header">
            <li className="cell" onClick={() => handleSort("order_number")}>
              注文番号
              {sortConfig.key === "order_number" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "order_number" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell" onClick={() => handleSort("customer_name")}>
              購入者名
              {sortConfig.key === "customer_name" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "customer_name" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell" onClick={() => handleSort("customer_phone")}>
              電話番号
              {sortConfig.key === "customer_phone" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "customer_phone" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell" onClick={() => handleSort("customer_email")}>
              メールアドレス
              {sortConfig.key === "customer_email" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "customer_email" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell" onClick={() => handleSort("postal_code")}>
              郵便番号
              {sortConfig.key === "postal_code" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "postal_code" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell" onClick={() => handleSort("address")}>
              住所
              {sortConfig.key === "address" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "address" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell items" onClick={() => handleSort("items")}>
              注文内容
              {sortConfig.key === "items" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "items" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell" onClick={() => handleSort("total_amount")}>
              金額
              {sortConfig.key === "total_amount" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "total_amount" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell" onClick={() => handleSort("payment_status")}>
              支払状態
              {sortConfig.key === "payment_status" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "payment_status" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
            <li className="cell" onClick={() => handleSort("delivery_status")}>
              受渡状態
              {sortConfig.key === "delivery_status" &&
                sortConfig.direction === "ascending" && (
                  <Icon name="triangle up" />
                )}
              {sortConfig.key === "delivery_status" &&
                sortConfig.direction === "descending" && (
                  <Icon name="triangle down" />
                )}
            </li>
          </ul>

          {sortedOrders.map((order) => (
            <ul
              key={order.id}
              className="list-grid"
            >
              <li
                className="cell copy"
                onClick={() => {
                  navigator.clipboard.writeText(order.order_number);
                }}
              >
                {order.order_number}
              </li>
              <li
                className="cell copy"
                onClick={() => {
                  navigator.clipboard.writeText(order.customer_name);
                }}
              >
                {order.customer_name}
              </li>
              <li
                className="cell copy"
                onClick={() => {
                  navigator.clipboard.writeText(order.customer_phone);
                }}
              >
                {order.customer_phone}
              </li>
              <li
                className="cell copy"
                onClick={() => {
                  navigator.clipboard.writeText(order.customer_email);
                }}
              >
                {order.customer_email}
              </li>
              <li
                className="cell copy"
                onClick={() => {
                  navigator.clipboard.writeText(order.postal_code);
                }}
              >
                {order.postal_code}
              </li>
              <li
                className="cell copy"
                onClick={() => {
                  navigator.clipboard.writeText(order.address);
                }}
              >
                {order.address}
              </li>
              <li
                className="cell items copy"
                onClick={() => {
                  navigator.clipboard.writeText(order.items.join("\n"));
                }}
              >
                {order.items.map((item, index) => (
                  <div key={index}>{item}</div>
                ))}
              </li>
              <li
                className="cell copy"
                onClick={() => {
                  navigator.clipboard.writeText(order.total_amount);
                }}
              >
                ¥{order.total_amount.toLocaleString()}
              </li>
              <li className="cell">
                {order.payment_status === "paid" ? "支払済" : "未払い"}
              </li>
              <li className="cell">
                <input
                  type="checkbox"
                  checked={order.delivery_status === "delivered"}
                  onChange={() => {
                    setDeliveredAnimeOn("");
                    handleDeliveryStatusChange(order.id, order.delivery_status);
                  }}
                />
              </li>
            </ul>
          ))}
          <p>
            {sortedOrders.filter((order) => order.delivery_status !== "delivered").length > 0 &&
              `受渡し待ち: ${
                sortedOrders.filter((order) => order.delivery_status !== "delivered").length
              }`}
          </p>
        </Grid.Column>
      </Grid>
    </>
  );
}

export default HoodieOrderListPage;
